import type { ModalProps as AntModalProps, ThemeConfig } from 'antd';
import { Modal as AntModal, ConfigProvider, theme } from 'antd';
import { useCallback, useEffect, useState } from 'react';

import type { ButtonsColors } from '@/lib/antd/ant-types';
import Button from '@/ui/button';
import { getCssVariable } from '@/utils';

import css from './styles.module.scss';

export interface IModalButtonProps extends AntModalProps {
  modalOpen?: boolean;
  setModalOpen?: (open: boolean) => void;
  link?: boolean;
  buttonText?: string;
  openButtonColor?: ButtonsColors;
  buttonDisabled?: boolean;
  additionClosedFunc?: () => void;
  canOpen?: boolean;
  canClose?: boolean;
  customButton?: JSX.Element;
  ownState?: boolean;
  onSuccessClose?: boolean;
  buttonProps?: React.ComponentProps<typeof Button>;
}

function ModalButton({
  modalOpen: externalModalOpen,
  setModalOpen: setExternalModalOpen = () => {},
  link,
  buttonText,
  openButtonColor = 'yellow',
  maskClosable = true,
  destroyOnClose = false,
  buttonDisabled,
  title,
  closeIcon = true,
  width = 580,
  zIndex: _, // TODO:
  keyboard = true,
  additionClosedFunc = () => {},
  canOpen = true,
  canClose = true,
  customButton,
  children,
  ownState = false,
  onSuccessClose = false,
  onOk,
  onCancel,
  buttonProps,
  ...props
}: IModalButtonProps) {
  const [internalModalOpen, setInternalModalOpen] = useState(false);

  const modalOpen = ownState ? internalModalOpen : externalModalOpen;
  const setModalOpen = ownState ? setInternalModalOpen : setExternalModalOpen;

  const handleModalOpen = () => {
    if (canOpen) setModalOpen(true);
  };
  const handleModalClose = useCallback(() => {
    if (canClose) setModalOpen(false);
  }, [canClose, setModalOpen]);

  useEffect(() => {
    if (onSuccessClose) {
      handleModalClose();
      additionClosedFunc();
    }
  }, [additionClosedFunc, handleModalClose, onSuccessClose]);

  //TODO сделать темы
  const [themes] = useState<'dark' | 'light'>('light');

  const darkmode = themes === 'dark' ? [theme.darkAlgorithm] : [];

  const config: ThemeConfig = {
    algorithm: darkmode,
  };

  return (
    <ConfigProvider theme={config}>
      {link ?
        <Button
          color="white"
          className={css.link}
          type="link"
          onClick={() => handleModalOpen()}
          disabled={buttonDisabled}
          {...buttonProps}
        >
          {buttonText}
        </Button>
      : customButton ?
        <div className={css.customButton} onClick={() => handleModalOpen()}>
          {customButton}
        </div>
      : <Button
          color={openButtonColor}
          onClick={() => handleModalOpen()}
          disabled={buttonDisabled}
          {...buttonProps}
        >
          {buttonText}
        </Button>
      }
      <AntModal
        maskClosable={maskClosable}
        destroyOnClose={destroyOnClose}
        centered
        footer={false}
        className={css.container}
        open={modalOpen}
        closeIcon={closeIcon}
        width={width}
        zIndex={Number(getCssVariable('--z-modal'))}
        keyboard={keyboard}
        onOk={(e) => {
          onOk?.(e);
          handleModalClose();
        }}
        onCancel={(e) => {
          onCancel?.(e);
          additionClosedFunc?.();
          handleModalClose();
        }}
        styles={{
          content: {
            padding: 25,
          },
        }}
        {...props}
      >
        {title && <div className={css.title}>{title}</div>}
        {children}
      </AntModal>
    </ConfigProvider>
  );
}

export default ModalButton;
